import { Controller } from "@hotwired/stimulus";
import { i18n } from "../lib/i18n";

/**
 * Field Form Controller
 *
 * Manages the dynamic field creation form with mode selection,
 * column picker, computed field templates, and custom SQL input.
 */
export default class extends Controller {
  static targets = [
    "modeSelector",
    "columnMode",
    "computedMode",
    "customMode",
    "columnSearch",
    "columnList",
    // Form fields
    "keyField",
    "labelField",
    "typeField",
    "expressionField",
    "relationshipPathField",
    "filterConfigField",
    // Capabilities
    "visibleField",
    "groupableField",
    "filterableField",
    "aggregatableField",
    "sortableField",
    // Template inputs
    "ageColumn",
    "firstNameColumn",
    "lastNameColumn",
    "datePartType",
    "datePartColumn",
    "durationStart",
    "durationEnd",
    "countLabel",
    "conditionColumn",
    "conditionOperator",
    "conditionValue",
    // Survey Response Filter inputs
    "surveyFilterMode",
    "existsModeFields",
    "directModeFields",
    "surveyJoinPath",
    "surveyPrimaryKey",
    "surveyForeignKey",
    "surveyQuestionIdCol",
    "surveyTextValueCol",
    "surveyNumericValueCol",
    "surveyDatetimeValueCol",
    "createLookupFields",
    // Foreign Key Lookup inputs
    "fkLookupModel",
    "fkLookupScope",
    "fkDisplayAttr",
    "fkLookupDisplayMethod",
    "fkValueAttr",
    "fkLinkable",
    "fkLinkPathContainer",
    "fkLinkPathHelper",
    "fkParentAttr",
    "fkOptgroupAttr",
    "fkExtraAttrs",
  ];

  static values = {
    table: String
  };

  // Column type to field type mapping
  typeMapping = {
    string: "string",
    text: "text",
    integer: "integer",
    bigint: "integer",
    decimal: "decimal",
    float: "decimal",
    date: "date",
    datetime: "datetime",
    boolean: "boolean",
    enum: "enum"
  };

  selectMode(event) {
    const mode = event.currentTarget.dataset.mode;

    this.hideModeSelector();

    switch (mode) {
      case "column":
        this.showColumnMode();
        break;
      case "computed":
        this.showComputedMode();
        break;
      case "custom":
        this.showCustomMode();
        break;
    }
  }

  backToModes() {
    this.hideAllModes();
    this.showModeSelector();
  }

  hideModeSelector() {
    if (this.hasModeSelectorTarget) {
      this.modeSelectorTarget.classList.add("hidden");
    }
  }

  showModeSelector() {
    if (this.hasModeSelectorTarget) {
      this.modeSelectorTarget.classList.remove("hidden");
    }
  }

  hideAllModes() {
    [this.columnModeTarget, this.computedModeTarget, this.customModeTarget].forEach((target) => {
      if (target) target.classList.add("hidden");
    });
  }

  showColumnMode() {
    this.hideAllModes();
    if (this.hasColumnModeTarget) {
      this.columnModeTarget.classList.remove("hidden");
      if (this.hasColumnSearchTarget) {
        this.columnSearchTarget.focus();
      }
    }
  }

  showComputedMode() {
    this.hideAllModes();
    if (this.hasComputedModeTarget) {
      this.computedModeTarget.classList.remove("hidden");
    }
  }

  showCustomMode() {
    this.hideAllModes();
    if (this.hasCustomModeTarget) {
      this.customModeTarget.classList.remove("hidden");
      if (this.hasKeyFieldTarget) {
        this.keyFieldTarget.focus();
      }
    }
  }

  switchToCustom() {
    this.showCustomMode();
  }

  filterColumns(event) {
    const search = event.target.value.toLowerCase();
    const items = this.columnListTarget.querySelectorAll("[data-column-item]");

    items.forEach((item) => {
      const name = item.dataset.columnName.toLowerCase();
      const type = item.dataset.columnType.toLowerCase();
      const matches = name.includes(search) || type.includes(search);
      item.classList.toggle("hidden", !matches);
    });
  }

  selectColumn(event) {
    const button = event.currentTarget;
    const columnName = button.dataset.columnName;
    const columnType = button.dataset.columnType;

    // Map database type to field type
    const fieldType = this.typeMapping[columnType] || "string";

    // Generate expression with table prefix
    const expression = this.tableValue ? `${this.tableValue}.${columnName}` : columnName;

    // Fill form fields
    this.setFieldValue("keyField", columnName);
    this.setFieldValue("labelField", this.humanize(columnName));
    this.setFieldValue("typeField", fieldType);
    this.setFieldValue("expressionField", expression);

    // Set sensible defaults based on type
    this.setCapabilities(fieldType, columnName);

    // Show custom mode with filled values
    this.showCustomMode();
  }

  setFieldValue(targetName, value) {
    const target = this[`has${this.capitalize(targetName)}Target`]
      ? this[`${targetName}Target`]
      : null;
    if (target) {
      target.value = value;
    }
  }

  setCheckbox(targetName, checked) {
    const target = this[`has${this.capitalize(targetName)}Target`]
      ? this[`${targetName}Target`]
      : null;
    if (target) {
      target.checked = checked;
    }
  }

  setCapabilities(fieldType, key) {
    // Smart defaults based on field type
    const isId = key === "id" || key.endsWith("_id");
    const isTimestamp = key.includes("_at") || key === "created_at" || key === "updated_at";
    const isName = key.includes("name") || key.includes("title") || key.includes("email");

    this.setCheckbox("visibleField", !isId && !key.startsWith("_"));
    this.setCheckbox("groupableField", !isId && !isName && fieldType !== "text");
    this.setCheckbox("filterableField", true);
    this.setCheckbox("aggregatableField", fieldType === "integer" || fieldType === "decimal" || isTimestamp);
    this.setCheckbox("sortableField", true);
  }

  // ==========================================
  // Template Handlers
  // ==========================================

  applyAgeTemplate() {
    if (!this.hasAgeColumnTarget) return;
    const column = this.ageColumnTarget.value;
    if (!column) {
      alert(i18n.t('field_form.select_date_column'));
      return;
    }

    const expression = `date_part('year', age(${this.tableValue}.${column}))`;

    this.setFieldValue("keyField", "age");
    this.setFieldValue("labelField", "Age");
    this.setFieldValue("typeField", "computed");
    this.setFieldValue("expressionField", expression);

    this.setCheckbox("visibleField", true);
    this.setCheckbox("groupableField", true);
    this.setCheckbox("filterableField", true);
    this.setCheckbox("aggregatableField", true);
    this.setCheckbox("sortableField", true);

    this.showCustomMode();
  }

  applyFullNameTemplate() {
    if (!this.hasFirstNameColumnTarget || !this.hasLastNameColumnTarget) return;
    const firstName = this.firstNameColumnTarget.value;
    const lastName = this.lastNameColumnTarget.value;

    if (!firstName || !lastName) {
      alert(i18n.t('field_form.select_both_name_columns'));
      return;
    }

    const expression = `${this.tableValue}.${firstName} || ' ' || ${this.tableValue}.${lastName}`;

    this.setFieldValue("keyField", "full_name");
    this.setFieldValue("labelField", "Full Name");
    this.setFieldValue("typeField", "string");
    this.setFieldValue("expressionField", expression);

    this.setCheckbox("visibleField", true);
    this.setCheckbox("groupableField", false);
    this.setCheckbox("filterableField", true);
    this.setCheckbox("aggregatableField", false);
    this.setCheckbox("sortableField", true);

    this.showCustomMode();
  }

  applyDatePartTemplate() {
    if (!this.hasDatePartTypeTarget || !this.hasDatePartColumnTarget) return;
    const partType = this.datePartTypeTarget.value;
    const column = this.datePartColumnTarget.value;

    if (!column) {
      alert(i18n.t('field_form.select_date_column'));
      return;
    }

    const expression = `date_part('${partType}', ${this.tableValue}.${column})`;
    const key = `${column}_${partType}`;
    const label = `${this.humanize(column)} ${this.capitalize(partType)}`;

    this.setFieldValue("keyField", key);
    this.setFieldValue("labelField", label);
    this.setFieldValue("typeField", "integer");
    this.setFieldValue("expressionField", expression);

    this.setCheckbox("visibleField", false);
    this.setCheckbox("groupableField", true);
    this.setCheckbox("filterableField", true);
    this.setCheckbox("aggregatableField", false);
    this.setCheckbox("sortableField", true);

    this.showCustomMode();
  }

  applyDurationTemplate() {
    if (!this.hasDurationStartTarget || !this.hasDurationEndTarget) return;
    const startCol = this.durationStartTarget.value;
    const endCol = this.durationEndTarget.value;

    if (!startCol || !endCol) {
      alert(i18n.t('field_form.select_both_date_columns'));
      return;
    }

    const expression = `EXTRACT(DAY FROM (${this.tableValue}.${endCol} - ${this.tableValue}.${startCol}))`;
    const key = `${startCol}_to_${endCol}_days`;

    this.setFieldValue("keyField", key);
    this.setFieldValue("labelField", "Duration (Days)");
    this.setFieldValue("typeField", "computed");
    this.setFieldValue("expressionField", expression);

    this.setCheckbox("visibleField", true);
    this.setCheckbox("groupableField", false);
    this.setCheckbox("filterableField", true);
    this.setCheckbox("aggregatableField", true);
    this.setCheckbox("sortableField", true);

    this.showCustomMode();
  }

  applyCountTemplate() {
    if (!this.hasCountLabelTarget) return;
    const label = this.countLabelTarget.value || "Record Count";

    // Generate a key from the label
    const key = label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");

    const expression = `${this.tableValue}.id`;

    this.setFieldValue("keyField", key);
    this.setFieldValue("labelField", label);
    this.setFieldValue("typeField", "computed");
    this.setFieldValue("expressionField", expression);

    this.setCheckbox("visibleField", false);
    this.setCheckbox("groupableField", false);
    this.setCheckbox("filterableField", false);
    this.setCheckbox("aggregatableField", true);
    this.setCheckbox("sortableField", false);

    this.showCustomMode();
  }

  applyConditionTemplate() {
    if (!this.hasConditionColumnTarget || !this.hasConditionOperatorTarget) return;
    const column = this.conditionColumnTarget.value;
    const operator = this.conditionOperatorTarget.value;
    const value = this.hasConditionValueTarget ? this.conditionValueTarget.value : "";

    if (!column) {
      alert(i18n.t('field_form.select_a_column'));
      return;
    }

    let condition;
    if (operator === "IS NOT NULL" || operator === "IS NULL") {
      condition = `${this.tableValue}.${column} ${operator}`;
    } else {
      const quotedValue = isNaN(value) ? `'${value}'` : value;
      condition = `${this.tableValue}.${column} ${operator} ${quotedValue}`;
    }

    const expression = `CASE WHEN ${condition} THEN 1 ELSE 0 END`;
    const key = `has_${column}`;
    const label = `Has ${this.humanize(column)}`;

    this.setFieldValue("keyField", key);
    this.setFieldValue("labelField", label);
    this.setFieldValue("typeField", "boolean");
    this.setFieldValue("expressionField", expression);

    this.setCheckbox("visibleField", true);
    this.setCheckbox("groupableField", true);
    this.setCheckbox("filterableField", true);
    this.setCheckbox("aggregatableField", true);
    this.setCheckbox("sortableField", true);

    this.showCustomMode();
  }

  // ==========================================
  // Survey Response Filter Template
  // ==========================================

  toggleSurveyFilterMode() {
    if (!this.hasSurveyFilterModeTarget) return;

    const mode = this.surveyFilterModeTarget.value;

    if (this.hasExistsModeFieldsTarget) {
      this.existsModeFieldsTarget.classList.toggle("hidden", mode !== "exists");
    }
    if (this.hasDirectModeFieldsTarget) {
      this.directModeFieldsTarget.classList.toggle("hidden", mode !== "direct");
    }
  }

  applySurveyResponseTemplate() {
    if (!this.hasSurveyFilterModeTarget) return;

    const mode = this.surveyFilterModeTarget.value;
    const createLookups = this.hasCreateLookupFieldsTarget && this.createLookupFieldsTarget.checked;

    let filterConfig, relationshipPath;

    if (mode === "exists") {
      // EXISTS subquery mode
      const joinPath = this.hasSurveyJoinPathTarget ? this.surveyJoinPathTarget.value : "respondents.responses";
      const primaryKey = this.hasSurveyPrimaryKeyTarget ? this.surveyPrimaryKeyTarget.value : `${this.tableValue}.id`;
      const foreignKey = this.hasSurveyForeignKeyTarget ? this.surveyForeignKeyTarget.value : "respondents.person_id";

      filterConfig = {
        join_path: joinPath,
        primary_key_expr: primaryKey,
        foreign_key_path: foreignKey
      };
      relationshipPath = joinPath;
    } else {
      // Direct column filter mode
      const questionIdCol = this.hasSurveyQuestionIdColTarget ? this.surveyQuestionIdColTarget.value : "responses.question_id";
      const textValueCol = this.hasSurveyTextValueColTarget ? this.surveyTextValueColTarget.value : "responses.text_value";
      const numericValueCol = this.hasSurveyNumericValueColTarget ? this.surveyNumericValueColTarget.value : "responses.numeric_value";
      const datetimeValueCol = this.hasSurveyDatetimeValueColTarget ? this.surveyDatetimeValueColTarget.value : "responses.datetime_value";

      filterConfig = {
        direct_filter: true,
        question_id_column: questionIdCol,
        text_value_column: textValueCol,
        numeric_value_column: numericValueCol,
        datetime_value_column: datetimeValueCol
      };
      relationshipPath = "self";
    }

    // Fill form fields for survey_response field
    this.setFieldValue("keyField", "survey_response");
    this.setFieldValue("labelField", "Survey Response");
    this.setFieldValue("typeField", "question_response");
    this.setFieldValue("expressionField", "responses.id");

    // Set relationship_path in the Advanced Options section
    const relationshipPathInput = this.element.querySelector('[name*="relationship_path"]');
    if (relationshipPathInput) {
      relationshipPathInput.value = relationshipPath;
    }

    // Set filter_config in the hidden field
    const filterConfigInput = this.element.querySelector('[name*="filter_config"]');
    if (filterConfigInput) {
      filterConfigInput.value = JSON.stringify(filterConfig);
    }

    // Set capabilities
    this.setCheckbox("visibleField", false);
    this.setCheckbox("groupableField", false);
    this.setCheckbox("filterableField", true);
    this.setCheckbox("aggregatableField", false);
    this.setCheckbox("sortableField", false);

    if (createLookups) {
      // Store data for creating lookup fields after form submission
      // We'll use a data attribute to signal the controller to create these
      const form = this.element.closest("form");
      if (form) {
        // Add hidden fields to signal lookup field creation
        this.addHiddenInput(form, "create_question_lookup", "true");
        this.addHiddenInput(form, "create_option_lookup", "true");
        this.addHiddenInput(form, "survey_filter_mode", mode);
      }
    }

    this.showCustomMode();
  }

  addHiddenInput(form, name, value) {
    // Remove existing if present
    const existing = form.querySelector(`input[name="${name}"]`);
    if (existing) existing.remove();

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  // ==========================================
  // Foreign Key Lookup Template
  // ==========================================

  toggleFkLinkable() {
    if (!this.hasFkLinkableTarget || !this.hasFkLinkPathContainerTarget) return;

    const isLinkable = this.fkLinkableTarget.checked;
    this.fkLinkPathContainerTarget.classList.toggle("hidden", !isLinkable);
  }

  applyForeignKeyTemplate() {
    const lookupModel = this.hasFkLookupModelTarget ? this.fkLookupModelTarget.value : "";
    const lookupScope = this.hasFkLookupScopeTarget ? this.fkLookupScopeTarget.value : "";
    const displayAttr = this.hasFkDisplayAttrTarget ? this.fkDisplayAttrTarget.value : "name";
    const lookupDisplayMethod = this.hasFkLookupDisplayMethodTarget ? this.fkLookupDisplayMethodTarget.value : "";
    const valueAttr = this.hasFkValueAttrTarget ? this.fkValueAttrTarget.value : "id";
    const isLinkable = this.hasFkLinkableTarget ? this.fkLinkableTarget.checked : false;
    const linkPathHelper = this.hasFkLinkPathHelperTarget ? this.fkLinkPathHelperTarget.value : "";

    if (!lookupModel) {
      alert(i18n.t('field_form.enter_lookup_model'));
      return;
    }

    // Build filter_config
    const filterConfig = {
      lookup_model: lookupModel,
      value_attribute: valueAttr,
      display_attribute: displayAttr
    };

    // Add lookup_display_method if provided (for resolving values in tables/charts)
    if (lookupDisplayMethod) {
      filterConfig.lookup_display_method = lookupDisplayMethod;
    }

    // Add optional attributes
    if (lookupScope) {
      filterConfig.lookup_scope = lookupScope;
    }

    // Add linkable configuration
    if (isLinkable) {
      filterConfig.linkable = true;
      if (linkPathHelper) {
        filterConfig.link_path_helper = linkPathHelper;
      }
    }

    const parentAttr = this.hasFkParentAttrTarget ? this.fkParentAttrTarget.value : "";
    if (parentAttr) {
      filterConfig.parent_attribute = parentAttr;
    }

    const optgroupAttr = this.hasFkOptgroupAttrTarget ? this.fkOptgroupAttrTarget.value : "";
    if (optgroupAttr) {
      filterConfig.optgroup_attribute = optgroupAttr;
    }

    const extraAttrs = this.hasFkExtraAttrsTarget ? this.fkExtraAttrsTarget.value : "";
    if (extraAttrs) {
      filterConfig.extra_attributes = extraAttrs.split(",").map((a) => a.trim()).filter((a) => a);
    }

    // Generate key from model name
    const key = lookupModel.toLowerCase().replace(/[^a-z0-9]+/g, "_") + "_lookup";
    const label = lookupModel;

    // Derive table name from model (simple pluralization)
    const tableName = lookupModel.toLowerCase() + "s";
    const expression = `${tableName}.id`;

    // Fill form fields
    this.setFieldValue("keyField", key);
    this.setFieldValue("labelField", label);
    this.setFieldValue("typeField", "foreign_key");
    this.setFieldValue("expressionField", expression);

    // Set relationship_path to lookup_helper (skips validation)
    const relationshipPathInput = this.element.querySelector('[name*="relationship_path"]');
    if (relationshipPathInput) {
      relationshipPathInput.value = "lookup_helper";
    }

    // Set filter_config in the hidden field
    const filterConfigInput = this.element.querySelector('[name*="filter_config"]');
    if (filterConfigInput) {
      filterConfigInput.value = JSON.stringify(filterConfig);
    }

    // Set capabilities - lookup fields are not used in queries directly
    this.setCheckbox("visibleField", false);
    this.setCheckbox("groupableField", false);
    this.setCheckbox("filterableField", false);
    this.setCheckbox("aggregatableField", false);
    this.setCheckbox("sortableField", false);

    this.showCustomMode();
  }

  // ==========================================
  // Utilities
  // ==========================================

  humanize(str) {
    return str
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase())
      .replace(/\bid\b/gi, "ID")
      .replace(/\bat\b/gi, "At");
  }

  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
